// src/components/common/MetricCard.js
import React from 'react';
import { Card, CardContent, Typography, Box, useTheme } from '@mui/material';

const MetricCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'primary', onClick }) => {
  const theme = useTheme();
  
  // Safe color helper function to avoid "Cannot read properties of undefined (reading 'main')" error
  const getBackgroundColor = (colorName, opacity) => {
    try {
      // Check if color exists in palette
      if (theme.palette[colorName] && theme.palette[colorName].main) {
        return `${theme.palette[colorName].main}${opacity}`;
      }
      // Default to primary if color doesn't exist
      return `${theme.palette.primary.main}${opacity}`;
    } catch (error) {
      // Ultimate fallback
      return `rgba(0, 0, 0, ${parseFloat(opacity, 16) / 255})`;
    }
  };
  
  // Safely get the main color
  const getMainColor = (colorName) => {
    try {
      if (theme.palette[colorName] && theme.palette[colorName].main) {
        return theme.palette[colorName].main;
      }
      return theme.palette.primary.main;
    } catch (error) {
      return '#3a86ff'; // Fallback blue color
    }
  };
  
  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };
  
  return (
    <Card 
      sx={{ 
        height: '100%', 
        borderRadius: '12px', 
        backgroundColor: theme.palette.background.paper,
        backgroundImage: `linear-gradient(135deg, ${getBackgroundColor(color, '0d')}, transparent)`,
        boxShadow: `0 8px 16px rgba(0, 0, 0, 0.1)`,
        position: 'relative',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': onClick ? {
          transform: 'translateY(-4px)',
          boxShadow: `0 12px 20px rgba(0, 0, 0, 0.15)`,
        } : {}
      }}
      onClick={handleClick}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: 80,
          height: 80,
          background: `linear-gradient(to bottom right, transparent 50%, ${getBackgroundColor(color, '08')} 50%)`,
          transform: 'rotate(-15deg) translate(20px, -20px)',
          borderRadius: '50%',
          zIndex: 0,
        }}
      />
      <CardContent sx={{ position: 'relative', zIndex: 1 }}>
        <Box 
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              bgcolor: getBackgroundColor(color, '1a'),
              color: getMainColor(color),
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
            }}
          >
            {Icon && <Icon />}
          </Box>
          <Typography variant="h6" fontWeight="medium">
            {title}
          </Typography>
        </Box>
        <Typography variant="h3" fontWeight="bold" mb={1} sx={{ color: getMainColor(color) }}>
          {value}
        </Typography>
        <Box display="flex" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
          {trend && trendValue && (
            <Box 
              sx={{ 
                ml: 'auto', 
                display: 'flex', 
                alignItems: 'center',
                borderRadius: 5,
                bgcolor: trend === 'up' ? getBackgroundColor('success', '1a') : getBackgroundColor('error', '1a'),
                color: trend === 'up' ? getMainColor('success') : getMainColor('error'),
                px: 1,
                py: 0.25,
              }}
            >
              {trend === 'up' ? (
                <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ fontSize: 16, mr: 0.5 }}>↑</Box>
                  <Typography variant="caption" fontWeight="medium">
                    {trendValue}
                  </Typography>
                </Box>
              ) : (
                <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ fontSize: 16, mr: 0.5 }}>↓</Box>
                  <Typography variant="caption" fontWeight="medium">
                    {trendValue}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default MetricCard;